import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { apiFetch, ApiRequestError } from "../lib/api.js";
import { PageLayout } from "../components/PageLayout.js";
import { ErrorState, LoadingState } from "../components/States.js";
import type { MemberCandidate } from "../components/MemberPicker.js";
import { FilterChip, HeaderCell, MultiSelectPopover, TextFilterPopover } from "../components/listFilters.js";
import {
  bulkDismissOutcomeReason,
  deleteRefusalReasonLabel,
  formatAge,
  formatDue,
  isDeletableSourceKind,
  roleBadge,
  roleLabel,
  severityPillClass,
  sourceKindLabel,
  statusPillClass,
  summarizeBulkDismiss,
  type ActionItemRow,
  type ActionItemSeverity,
  type ActionItemSourceKind,
  type ActionItemStatus,
  type ActionItemCode,
  type BulkDeleteResponse,
  type BulkDismissResponse,
  type BulkDismissResult,
  type RASCIRole
} from "../lib/actionItems.js";
import {
  applyViewState,
  readViewState,
  serializeViewState,
  viewStateInUrl,
  writeViewState
} from "../lib/listViewMemory.js";
import { useConfirm } from "../components/ConfirmDialog.js";
import { useToast } from "../components/Toast.js";

/**
 * Action Items list (ADR-0053, redesigned per ADR-0068 feedback 2026-04-23).
 *
 * Route: `/app/action-items`
 *
 * Design:
 *   - Column-header funnels carry the fine-grained filters. A slim toolbar
 *     (added 2026-08-22) carries the three everyday controls: "Show closed"
 *     (default OFF — resolved/dismissed stay out of the default view),
 *     "My items" (any RASCI role), and a title search box. All three are
 *     shorthands over the same URL params the funnels use, so the chip
 *     strip and popovers stay in sync. The "Show closed" box reports
 *     whether closed items can currently appear — an explicit Status
 *     funnel selection drives it too — and toggling it clears any funnel
 *     selection (the box is the coarser instrument).
 *   - Columns are sortable — click the column name to toggle asc/desc/off.
 *   - Active filters summarized as chips above the table; `× clear all` clears
 *     them in one click.
 *   - URL carries every filter + sort so deep-links and refresh work.
 *   - Server-side filter + sort. When sort is non-default (not `created_at`),
 *     pagination is disabled in v1 (the hub returns `next_cursor: null`).
 *   - Rows are multi-selectable, and a selection can be dismissed in one
 *     pass with a shared code + note (2026-08-17). The hub authorizes and
 *     transitions each id on its own, so the outcome is per item — the
 *     result panel reports every id that didn't get dismissed and why,
 *     rather than a single pass/fail.
 */

// Exported: the /my-action-items redirect in TenantApp.tsx builds the
// "my items" URL (`my_rasci_roles=` all five roles) from this list so it
// stays in lockstep with the My-items toggle below.
export const RASCI_ROLES: RASCIRole[] = ["responsible", "accountable", "support", "consulted", "informed"];
const SEVERITIES: ActionItemSeverity[] = ["critical", "high", "medium", "low"];
const SOURCE_KINDS: ActionItemSourceKind[] = ["manual", "watch", "pattern_review", "user_provision", "system"];
const STATUSES: ActionItemStatus[] = ["open", "acknowledged", "in_progress", "deferred", "resolved", "dismissed"];
const OPEN_STATUSES: ActionItemStatus[] = ["open", "acknowledged", "in_progress", "deferred"];

// Every URL param that makes up "the view I had": the filters, the toolbar
// shorthands, the lens, and the sort. Remembered per signed-in account for
// the browser session so leaving for an item detail and coming back doesn't
// reset the list (2026-08-24). Order is fixed — the serializer depends on it.
const REMEMBERED_PARAMS = [
  "title_contains",
  "severities_csv",
  "source_kinds_csv",
  "item_type_codes_csv",
  "statuses_csv",
  "show_closed",
  "assignee_user_ids_csv",
  "my_rasci_roles",
  "lens",
  "due_from",
  "due_to",
  "include_no_due",
  "sort",
  "order"
] as const;

function viewMemoryKey(accountId: string): string {
  return `atlas.actionItems.view:${accountId}`;
}

type SortColumn =
  | "title"
  | "source_kind"
  | "item_type_code_id"
  | "severity"
  | "status"
  | "assignee_user_id"
  | "due_at"
  | "created_at";

type SortOrder = "asc" | "desc";

const PAGE_SIZE = 50;

interface ListResponse {
  items: ActionItemRow[];
  next_cursor: { createdAt: string; id: string } | null;
}

function displayFor(id: string | null, dir: Map<string, MemberCandidate>): string {
  if (!id) return "—";
  const c = dir.get(id);
  return c?.display_name ?? `${id.slice(0, 8)}…`;
}

function parseCsv(raw: string | null): string[] {
  if (!raw) return [];
  return raw.split(",").map((s) => s.trim()).filter((s) => s.length > 0);
}

function parseCsvTyped<T extends string>(raw: string | null, allowed: readonly T[]): T[] {
  return parseCsv(raw).filter((s): s is T => (allowed as readonly string[]).includes(s));
}

function severityLabel(s: ActionItemSeverity): string {
  return s.charAt(0).toUpperCase() + s.slice(1);
}
function statusLabel(s: ActionItemStatus): string {
  return s.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export function ActionItemsList(props: { entitlement?: string; callerAccountId: string }) {
  const canCreate = props.entitlement === "builder" || props.entitlement === "admin";
  const isAdmin = props.entitlement === "admin";
  const [searchParams, setSearchParams] = useSearchParams();

  // Filters — each reads from URL; setters rewrite the URL.
  const titleContains = searchParams.get("title_contains") ?? "";
  const severities = parseCsvTyped(searchParams.get("severities_csv"), SEVERITIES);
  const sourceKinds = parseCsvTyped(searchParams.get("source_kinds_csv"), SOURCE_KINDS);
  const itemTypeIds = parseCsv(searchParams.get("item_type_codes_csv"));
  const statuses = parseCsvTyped(searchParams.get("statuses_csv"), STATUSES);
  const assigneeIds = parseCsv(searchParams.get("assignee_user_ids_csv"));
  const myRasci = parseCsvTyped(searchParams.get("my_rasci_roles"), RASCI_ROLES);
  // ADR-0173 §3.5 — the My org / Everyone lens. Absent param = "org" (the
  // ruled default for everyone; a leaf user's My org is just their own items).
  // A lens, not a boundary: read authority stays tenant-wide.
  const lens: "org" | "all" = searchParams.get("lens") === "all" ? "all" : "org";
  const dueFrom = searchParams.get("due_from") ?? "";
  const dueTo = searchParams.get("due_to") ?? "";
  const includeNoDue = searchParams.get("include_no_due") !== "0"; // default true

  // Toolbar shorthands. Hiding closed is the default view: with no explicit
  // Status funnel selection and no show_closed=1 in the URL, the list only
  // fetches non-closed statuses. An explicit Status selection always wins.
  // The "Show closed" checkbox reports whether closed items can actually
  // appear under the current filters, whichever instrument set them.
  const showClosed = searchParams.get("show_closed") === "1";
  const hideClosed = !showClosed && statuses.length === 0;
  const closedVisible = statuses.length > 0
    ? statuses.some((s) => s === "resolved" || s === "dismissed")
    : showClosed;
  // "My items" = holding ANY RASCI role, i.e. all five roles selected.
  const myItemsOnly = RASCI_ROLES.every((r) => myRasci.includes(r));

  // Sort — always on. Default is Age (created_at) descending and the UI
  // reflects that even when the URL doesn't carry sort/order params. Click
  // any column header to swap in that column (desc); click again to flip
  // asc/desc. No "unsorted" state.
  const rawSort = searchParams.get("sort") as SortColumn | null;
  const sort: SortColumn = rawSort ?? "created_at";
  const order: SortOrder = searchParams.get("order") === "asc" ? "asc" : "desc";
  const isDefaultSort = sort === "created_at";

  // Single-param setter used by filters + sort.
  const setParams = useCallback(
    (updates: Record<string, string | null>) => {
      setSearchParams(
        (prev) => {
          const p = new URLSearchParams(prev);
          for (const [k, v] of Object.entries(updates)) {
            if (v === null || v === "") p.delete(k);
            else p.set(k, v);
          }
          return p;
        },
        { replace: true }
      );
    },
    [setSearchParams]
  );

  // ---- View memory (session, per account) ---------------------------------
  // Restore on mount when the URL carries no view of its own; a deep link or
  // an in-app redirect (e.g. /my-action-items) always wins. Persist on every
  // change, including "Clear all" — an empty view is a remembered view.
  // `memoryResolved` also gates the first fetch below: restoring rewrites the
  // URL, and firing a request against the pre-restore params would leave two
  // list requests racing to land in the same table.
  const memoryKey = viewMemoryKey(props.callerAccountId);
  const [memoryResolved, setMemoryResolved] = useState(false);
  const pendingRestoreRef = useRef<string | null>(null);

  useEffect(() => {
    const stored = readViewState(memoryKey);
    if (stored && !viewStateInUrl(searchParams, REMEMBERED_PARAMS)) {
      pendingRestoreRef.current = stored;
      setSearchParams((prev) => applyViewState(prev, stored, REMEMBERED_PARAMS), { replace: true });
    }
    setMemoryResolved(true);
    // Mount-only: `searchParams` is read once, as the URL the user arrived on.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [memoryKey]);

  useEffect(() => {
    if (!memoryResolved) return;
    const serialized = serializeViewState(searchParams, REMEMBERED_PARAMS);
    // While a restore is in flight, the params still describe the pre-restore
    // view — don't write that over the memory we're about to apply.
    if (pendingRestoreRef.current !== null) {
      if (serialized !== pendingRestoreRef.current) return;
      pendingRestoreRef.current = null;
    }
    writeViewState(memoryKey, serialized);
  }, [searchParams, memoryKey, memoryResolved]);

  // Reference data: RASCI directory + item-type codes. Fetched once per mount.
  const [directory, setDirectory] = useState<Map<string, MemberCandidate>>(new Map());
  const [itemTypeCodes, setItemTypeCodes] = useState<ActionItemCode[]>([]);
  useEffect(() => {
    void (async () => {
      try {
        const [dirRes, itRes] = await Promise.all([
          apiFetch<{ candidates: MemberCandidate[] }>("/api/tenant/rasci-candidates").catch(() => ({ candidates: [] as MemberCandidate[] })),
          apiFetch<{ codes: ActionItemCode[] }>("/api/tenant/action-item-codes/item-types").catch(() => ({ codes: [] as ActionItemCode[] }))
        ]);
        const m = new Map<string, MemberCandidate>();
        for (const c of dirRes.candidates) m.set(c.id, c);
        setDirectory(m);
        setItemTypeCodes(itRes.codes);
      } catch {
        /* non-fatal — raw ids shown */
      }
    })();
  }, []);

  // Fetch state. `initialLoading` covers the first mount (show full
  // LoadingState). `refreshing` covers re-fetches driven by filter or sort
  // changes — we keep the previous rows on screen and show a subtle
  // indicator instead of unmounting the table. This prevents the screen
  // from flashing, and keeps any open filter popover mounted while the
  // user is mid-click.
  const [items, setItems] = useState<ActionItemRow[]>([]);
  const [cursor, setCursor] = useState<{ createdAt: string; id: string } | null>(null);
  const [initialLoading, setInitialLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const loadedOnceRef = useRef(false);

  const queryString = useMemo(() => {
    const p = new URLSearchParams();
    if (titleContains.trim()) p.set("title_contains", titleContains.trim());
    if (severities.length) p.set("severities_csv", severities.join(","));
    if (sourceKinds.length) p.set("source_kinds_csv", sourceKinds.join(","));
    if (itemTypeIds.length) p.set("item_type_codes_csv", itemTypeIds.join(","));
    const effectiveStatuses = statuses.length ? statuses : hideClosed ? OPEN_STATUSES : [];
    if (effectiveStatuses.length) p.set("statuses_csv", effectiveStatuses.join(","));
    if (assigneeIds.length) p.set("assignee_user_ids_csv", assigneeIds.join(","));
    if (myRasci.length) p.set("my_rasci_roles", myRasci.join(","));
    if (lens === "org") p.set("assignee_subtree_of", props.callerAccountId);
    if (dueFrom) p.set("due_from", dueFrom);
    if (dueTo) p.set("due_to", dueTo);
    if (!includeNoDue) p.set("include_no_due", "0");
    if (rawSort) {
      p.set("sort", rawSort);
      p.set("order", order);
    }
    p.set("limit", String(PAGE_SIZE));
    return p.toString();
  }, [titleContains, severities, sourceKinds, itemTypeIds, statuses, hideClosed, assigneeIds, myRasci, lens, props.callerAccountId, dueFrom, dueTo, includeNoDue, rawSort, order]);

  const loadFirstPage = useCallback(async () => {
    // Initial mount = full loading state. Re-fetch = non-destructive; keep
    // current rows mounted and just flash a small indicator.
    if (loadedOnceRef.current) {
      setRefreshing(true);
    } else {
      setInitialLoading(true);
    }
    setError(null);
    try {
      const res = await apiFetch<ListResponse>(`/api/tenant/action-items?${queryString}`);
      setItems(res.items);
      setCursor(res.next_cursor);
      loadedOnceRef.current = true;
    } catch (e) {
      setError(e instanceof ApiRequestError ? e.message : e instanceof Error ? e.message : String(e));
    } finally {
      setInitialLoading(false);
      setRefreshing(false);
    }
  }, [queryString]);

  useEffect(() => {
    if (!memoryResolved) return; // wait for a restored view before the first fetch
    void loadFirstPage();
  }, [loadFirstPage, memoryResolved]);

  async function loadMore() {
    if (!cursor || loadingMore) return;
    setLoadingMore(true);
    try {
      const p = new URLSearchParams(queryString);
      p.set("cursor_created_at", cursor.createdAt);
      p.set("cursor_id", cursor.id);
      const res = await apiFetch<ListResponse>(`/api/tenant/action-items?${p.toString()}`);
      setItems((prev) => [...prev, ...res.items]);
      setCursor(res.next_cursor);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoadingMore(false);
    }
  }

  // Sort toggle — tables are always sorted. Click a different column to
  // switch to it (desc by default). Click the current sorted column to flip
  // between desc and asc. No "unsorted" state.
  function toggleSort(col: SortColumn) {
    if (sort !== col) {
      setParams({ sort: col, order: "desc" });
      return;
    }
    setParams({ sort: col, order: order === "desc" ? "asc" : "desc" });
  }

  const itemTypeLabel = (id: string): string => {
    const it = itemTypeCodes.find((c) => c.id === id);
    return it?.label ?? `${id.slice(0, 8)}…`;
  };

  // Pre-compute filter state per column for iconActive.
  const filterActive = {
    title: titleContains.trim().length > 0,
    myRasci: myRasci.length > 0,
    source: sourceKinds.length > 0,
    type: itemTypeIds.length > 0,
    severity: severities.length > 0,
    status: statuses.length > 0,
    assignee: assigneeIds.length > 0,
    due: !!dueFrom || !!dueTo || !includeNoDue,
    // Not a filter — the *absence* of the default one. Chipped so every
    // departure from the default view is visible and removable in one place.
    showingClosed: showClosed
  };
  const anyFilterActive = Object.values(filterActive).some((v) => v);

  const assigneeOptions = useMemo(
    () => Array.from(directory.values()).filter((c) => c.kind === "user"),
    [directory]
  );

  // ---- Selection + bulk actions ------------------------------------------
  // A row is selectable when SOME bulk action could apply to it: it's open
  // enough to dismiss, or it's watch-generated and the caller can delete it.
  // A closed watch item is still selectable for an admin precisely because
  // deleting it is the point — junk that was dismissed earlier shouldn't be
  // stranded. If a row closes while the list is on screen, bulk dismissal
  // reports it as skipped rather than erroring, which the result panel shows.
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [dismissOpen, setDismissOpen] = useState(false);
  const [dismissResults, setDismissResults] = useState<BulkDismissResult[] | null>(null);

  const canSelect = useCallback(
    (it: ActionItemRow): boolean => {
      const closed = it.status === "resolved" || it.status === "dismissed";
      if (!closed) return true;
      return isAdmin && isDeletableSourceKind(it.source_kind);
    },
    [isAdmin]
  );

  const selectableIds = useMemo(
    () => items.filter((it) => canSelect(it)).map((it) => it.id),
    [items, canSelect]
  );
  // Selection is keyed by id, so a row that scrolls out of the current page
  // would keep a stale entry. Prune to what's on screen whenever the rows
  // change (filter, sort, refresh) — the count in the button is then always
  // the count the user can see.
  useEffect(() => {
    setSelected((prev) => {
      if (prev.size === 0) return prev;
      const visible = new Set(selectableIds);
      const next = new Set([...prev].filter((id) => visible.has(id)));
      return next.size === prev.size ? prev : next;
    });
  }, [selectableIds]);

  const selectedItems = useMemo(
    () => items.filter((it) => selected.has(it.id)),
    [items, selected]
  );

  function toggleOne(id: string) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  function toggleAll() {
    setSelected((prev) => (prev.size === selectableIds.length ? new Set() : new Set(selectableIds)));
  }

  // ---- Hard delete (admin, watch-sourced only) ---------------------------
  // Dismissing records a considered decision; deleting says the item should
  // never have existed. Only watch-generated items qualify, and only an
  // admin can do it — both rules are the hub's, mirrored here so the button
  // never promises something the server will refuse.
  const toast = useToast();
  const confirm = useConfirm();
  const [deleteBusy, setDeleteBusy] = useState(false);

  const deletableSelected = useMemo(
    () => selectedItems.filter((it) => isDeletableSourceKind(it.source_kind)),
    [selectedItems]
  );
  const undeletableSelected = selectedItems.length - deletableSelected.length;

  async function deleteSelected() {
    if (deletableSelected.length === 0) return;
    const n = deletableSelected.length;
    const ok = await confirm.confirm({
      title: `Delete ${n} action item${n === 1 ? "" : "s"}?`,
      detail:
        `${n} watch-generated item${n === 1 ? "" : "s"} will be permanently deleted, along with ` +
        `their history, comments, and notifications. This cannot be undone.` +
        (undeletableSelected > 0
          ? ` ${undeletableSelected} other selected item${undeletableSelected === 1 ? "" : "s"} ` +
            `${undeletableSelected === 1 ? "is" : "are"} not watch-generated and will be left alone — dismiss ` +
            `${undeletableSelected === 1 ? "it" : "them"} instead.`
          : "") +
        ` Deleting also resets the watch's memory of ${n === 1 ? "this item" : "these items"}: if the underlying` +
        ` ${n === 1 ? "row still matches" : "rows still match"}, the watch will raise ${n === 1 ? "a fresh item" : "fresh items"}` +
        ` on its next scheduled run.`,
      confirmLabel: "Delete",
      destructive: true,
      requireTypedConfirmation: { value: "DELETE", label: "Type DELETE to confirm:", copyable: false }
    });
    if (!ok) return;

    setDeleteBusy(true);
    try {
      const res = await apiFetch<BulkDeleteResponse>("/api/tenant/action-items/bulk-delete", {
        method: "POST",
        body: { ids: deletableSelected.map((it) => it.id) }
      });
      const failed = res.refused.filter((r) => r.reason !== "not_deletable_source");
      toast.push({
        kind: failed.length > 0 ? "error" : "success",
        message:
          failed.length > 0
            ? `Deleted ${res.deleted.length}; ${failed.length} could not be deleted (${failed
                .map((r) => deleteRefusalReasonLabel(r.reason).toLowerCase())
                .join(", ")}).`
            : `Deleted ${res.deleted.length} action item${res.deleted.length === 1 ? "" : "s"}.` +
              ((res.watch_tracking_cleared ?? 0) > 0
                ? " Watch tracking was reset — rows that still match will raise fresh items on the next run."
                : "")
      });
      setSelected(new Set());
      setDismissResults(null);
      await loadFirstPage();
    } catch (e) {
      toast.push({ kind: "error", message: e instanceof Error ? e.message : String(e) });
    } finally {
      setDeleteBusy(false);
    }
  }

  function clearAllFilters() {
    setParams({
      title_contains: null,
      severities_csv: null,
      source_kinds_csv: null,
      item_type_codes_csv: null,
      statuses_csv: null,
      show_closed: null,
      assignee_user_ids_csv: null,
      my_rasci_roles: null,
      due_from: null,
      due_to: null,
      include_no_due: null
    });
  }

  // ---- Toolbar controls ---------------------------------------------------
  // The search box edits a local draft and applies on Enter or blur — the
  // list is server-filtered, so applying per keystroke would fire a fetch
  // per character.
  const [searchDraft, setSearchDraft] = useState(titleContains);
  useEffect(() => setSearchDraft(titleContains), [titleContains]);

  function applySearch() {
    const v = searchDraft.trim();
    if (v === titleContains.trim()) return;
    setParams({ title_contains: v || null });
  }

  function toggleShowClosed() {
    // Either direction clears any explicit Status selection — the two drive
    // the same underlying filter, and the box is the coarser instrument.
    if (closedVisible) setParams({ show_closed: null, statuses_csv: null });
    else setParams({ show_closed: "1", statuses_csv: null });
  }

  function toggleMyItems() {
    setParams({ my_rasci_roles: myItemsOnly ? null : RASCI_ROLES.join(",") });
  }

  return (
    <PageLayout title="Action Items">
      <div style={{ display: "grid", gap: 12 }}>
        {/* Header row: everyday filters on the left, bulk actions + New on
            the right. Fine-grained filtering stays in the column funnels. */}
        <div className="toolbar">
          <div className="toolbarGroup">
            <select
              className="select"
              value={lens}
              onChange={(e) => setParams({ lens: e.target.value === "all" ? "all" : null })}
              title="My org shows items assigned to you, everyone in your org subtree, and contacts they proxy"
              aria-label="Assignee lens"
              style={{ width: "auto", fontSize: 13 }}
            >
              <option value="org">My org</option>
              <option value="all">Everyone</option>
            </select>
            <label
              title="Include resolved and dismissed items. Off by default; the Status column filter can also control which statuses show."
              style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 13, cursor: "pointer", whiteSpace: "nowrap" }}
            >
              <input type="checkbox" checked={closedVisible} onChange={toggleShowClosed} />
              Show closed
            </label>
            <label
              title="Only items where you hold a RASCI role — the My RASCI column shows which"
              style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 13, cursor: "pointer", whiteSpace: "nowrap" }}
            >
              <input type="checkbox" checked={myItemsOnly} onChange={toggleMyItems} />
              My items
            </label>
            <input
              className="input"
              type="search"
              placeholder="Search titles…"
              title="Search action item titles — press Enter to apply"
              value={searchDraft}
              onChange={(e) => setSearchDraft(e.target.value)}
              onBlur={applySearch}
              onKeyDown={(e) => {
                if (e.key === "Enter") applySearch();
              }}
              style={{ width: 340, fontSize: 13, flexShrink: 0 }}
            />
          </div>
          <div className="toolbarGroup" style={{ marginLeft: "auto" }}>
            {selected.size > 0 && (
              <>
                <span className="muted" style={{ fontSize: 12 }}>{selected.size} selected</span>
                <button type="button" className="btn" onClick={() => setSelected(new Set())}>
                  Clear selection
                </button>
                <button type="button" className="btn" onClick={() => setDismissOpen(true)}>
                  Dismiss selected ({selected.size})
                </button>
                {/* Delete is admin-only and watch-sourced-only. When nothing
                    selected qualifies, the button stays visible but disabled
                    and says why — a button that silently vanishes reads as a
                    bug, and an admin needs to know deletion has a boundary. */}
                {isAdmin && (
                  <button
                    type="button"
                    className="btn btnDanger"
                    disabled={deleteBusy || deletableSelected.length === 0}
                    title={
                      deletableSelected.length === 0
                        ? "Only watch-generated items can be deleted. Dismiss the others instead."
                        : `Permanently delete ${deletableSelected.length} watch-generated item${deletableSelected.length === 1 ? "" : "s"}`
                    }
                    onClick={() => void deleteSelected()}
                  >
                    {deleteBusy ? "Deleting…" : `Delete selected (${deletableSelected.length})`}
                  </button>
                )}
              </>
            )}
            {canCreate && (
              <Link className="btn btnPrimary" to="/app/action-items/new">
                + New action item
              </Link>
            )}
          </div>
        </div>

        {/* Outcome of the last bulk dismissal. Stays up until dismissed —
            a partial result is something the user needs to read, not a
            toast that vanishes. */}
        {dismissResults && (
          <BulkDismissResultPanel results={dismissResults} onClose={() => setDismissResults(null)} />
        )}

        {/* Active-filter summary chip strip */}
        {anyFilterActive && (
          <div className="filterSummaryStrip">
            <span>Filtered by:</span>
            {filterActive.title && (
              <FilterChip label={`Title contains "${titleContains.trim()}"`} onClear={() => setParams({ title_contains: null })} />
            )}
            {filterActive.myRasci && (
              <FilterChip
                label={myItemsOnly ? "My items" : `My RASCI: ${myRasci.map((r) => roleBadge(r)).join("/")}`}
                onClear={() => setParams({ my_rasci_roles: null })}
              />
            )}
            {filterActive.severity && (
              <FilterChip
                label={`Severity: ${severities.map(severityLabel).join(", ")}`}
                onClear={() => setParams({ severities_csv: null })}
              />
            )}
            {filterActive.status && (
              <FilterChip
                label={`Status: ${statuses.map(statusLabel).join(", ")}`}
                onClear={() => setParams({ statuses_csv: null })}
              />
            )}
            {filterActive.source && (
              <FilterChip
                label={`Source: ${sourceKinds.map(sourceKindLabel).join(", ")}`}
                onClear={() => setParams({ source_kinds_csv: null })}
              />
            )}
            {filterActive.type && (
              <FilterChip
                label={`Type: ${itemTypeIds.map(itemTypeLabel).join(", ")}`}
                onClear={() => setParams({ item_type_codes_csv: null })}
              />
            )}
            {filterActive.assignee && (
              <FilterChip
                label={`Assigned to: ${assigneeIds.map((id) => displayFor(id, directory)).join(", ")}`}
                onClear={() => setParams({ assignee_user_ids_csv: null })}
              />
            )}
            {filterActive.due && (
              <FilterChip
                label={describeDueFilter(dueFrom, dueTo, includeNoDue)}
                onClear={() => setParams({ due_from: null, due_to: null, include_no_due: null })}
              />
            )}
            {filterActive.showingClosed && (
              <FilterChip label="Showing closed" onClear={() => setParams({ show_closed: null })} />
            )}
            <button type="button" className="filterClearAll" onClick={clearAllFilters}>Clear all</button>
          </div>
        )}

        {error ? <ErrorState detail={error} /> : null}
        {initialLoading && !error && <LoadingState />}
        {!initialLoading && !error && items.length === 0 && !refreshing && (
          <div className="adminListEmpty">
            {lens === "org"
              ? "No action items in your org."
              : hideClosed && !anyFilterActive
                ? "No open action items. Check “Show closed” to see resolved and dismissed items."
                : "No action items match these filters."}
          </div>
        )}
        {!initialLoading && !error && (items.length > 0 || refreshing) && (
          <div className="tableWrap" style={{ position: "relative" }}>
            {refreshing && (
              <div
                className="muted"
                style={{
                  position: "absolute", top: 4, right: 8, fontSize: 11,
                  letterSpacing: 0.04, opacity: 0.8
                }}
                aria-live="polite"
              >
                Refreshing…
              </div>
            )}
            <table className="table">
              <thead>
                <tr>
                  <th style={{ width: 28 }}>
                    <input
                      type="checkbox"
                      aria-label="Select all open action items"
                      title={selectableIds.length === 0 ? "Nothing on this page can be dismissed" : "Select all"}
                      disabled={selectableIds.length === 0}
                      checked={selectableIds.length > 0 && selected.size === selectableIds.length}
                      ref={(el) => {
                        if (el) el.indeterminate = selected.size > 0 && selected.size < selectableIds.length;
                      }}
                      onChange={() => toggleAll()}
                    />
                  </th>
                  <HeaderCell
                    title="Title" sortKey="title" currentSort={sort} currentOrder={order}
                    onToggleSort={toggleSort} isFilterActive={filterActive.title}
                  >
                    <TextFilterPopover
                      value={titleContains}
                      onApply={(v) => setParams({ title_contains: v || null })}
                    />
                  </HeaderCell>

                  <HeaderCell title="My RASCI" isFilterActive={filterActive.myRasci}>
                    <MultiSelectPopover
                      title="Filter by your RASCI role"
                      options={RASCI_ROLES.map((r) => ({ id: r, label: `${roleBadge(r)} — ${roleLabel(r)}` }))}
                      selected={myRasci}
                      onChange={(next) => setParams({ my_rasci_roles: next.length ? next.join(",") : null })}
                    />
                  </HeaderCell>

                  <HeaderCell
                    title="Type" sortKey="item_type_code_id" currentSort={sort} currentOrder={order}
                    onToggleSort={toggleSort} isFilterActive={filterActive.type}
                  >
                    <MultiSelectPopover
                      title="Filter by type"
                      options={itemTypeCodes.map((c) => ({ id: c.id, label: c.label }))}
                      selected={itemTypeIds}
                      onChange={(next) => setParams({ item_type_codes_csv: next.length ? next.join(",") : null })}
                      emptyMessage="No item-type codes defined yet."
                    />
                  </HeaderCell>

                  <HeaderCell
                    title="Severity" sortKey="severity" currentSort={sort} currentOrder={order}
                    onToggleSort={toggleSort} isFilterActive={filterActive.severity}
                  >
                    <MultiSelectPopover
                      title="Filter by severity"
                      options={SEVERITIES.map((s) => ({ id: s, label: severityLabel(s) }))}
                      selected={severities}
                      onChange={(next) => setParams({ severities_csv: next.length ? next.join(",") : null })}
                    />
                  </HeaderCell>

                  <HeaderCell
                    title="Status" sortKey="status" currentSort={sort} currentOrder={order}
                    onToggleSort={toggleSort} isFilterActive={filterActive.status}
                  >
                    <MultiSelectPopover
                      title="Filter by status"
                      options={STATUSES.map((s) => ({ id: s, label: statusLabel(s) }))}
                      selected={statuses}
                      onChange={(next) => setParams({ statuses_csv: next.length ? next.join(",") : null })}
                    />
                  </HeaderCell>

                  <HeaderCell
                    title="Assigned to" sortKey="assignee_user_id" currentSort={sort} currentOrder={order}
                    onToggleSort={toggleSort} isFilterActive={filterActive.assignee}
                  >
                    <MultiSelectPopover
                      title="Filter by who it's assigned to"
                      options={assigneeOptions.map((u) => ({ id: u.id, label: u.display_name }))}
                      selected={assigneeIds}
                      onChange={(next) => setParams({ assignee_user_ids_csv: next.length ? next.join(",") : null })}
                      emptyMessage="No users available."
                    />
                  </HeaderCell>

                  <HeaderCell
                    title="Due" sortKey="due_at" currentSort={sort} currentOrder={order}
                    onToggleSort={toggleSort} isFilterActive={filterActive.due}
                  >
                    <DueDateFilterPopover
                      dueFrom={dueFrom}
                      dueTo={dueTo}
                      includeNoDue={includeNoDue}
                      onApply={({ from, to, noDue }) =>
                        setParams({
                          due_from: from || null,
                          due_to: to || null,
                          include_no_due: noDue ? null : "0"
                        })
                      }
                    />
                  </HeaderCell>

                  <HeaderCell
                    title="Age" sortKey="created_at" currentSort={sort} currentOrder={order}
                    onToggleSort={toggleSort}
                  />
                </tr>
              </thead>
              <tbody>
                {items.map((it) => {
                  const selectable = canSelect(it);
                  return (
                  <tr key={it.id}>
                    <td>
                      <input
                        type="checkbox"
                        aria-label={`Select ${it.title}`}
                        title={
                          selectable
                            ? `Select ${it.title}`
                            : `Already ${statusLabel(it.status).toLowerCase()} — nothing left to do in bulk`
                        }
                        disabled={!selectable}
                        checked={selected.has(it.id)}
                        onChange={() => toggleOne(it.id)}
                      />
                    </td>
                    <td>
                      <Link className="adminListName" to={`/app/action-items/${it.id}`}>
                        {it.title}
                      </Link>
                    </td>
                    <td><MyRasciCell roles={it.my_rasci_roles ?? []} /></td>
                    <td>{itemTypeLabel(it.item_type_code_id)}</td>
                    <td><span className={`pill ${severityPillClass(it.severity)}`}>{it.severity}</span></td>
                    <td><span className={`pill ${statusPillClass(it.status)}`}>{statusLabel(it.status)}</span></td>
                    <td>{displayFor(it.assignee_user_id, directory)}</td>
                    <td>{formatDue(it.due_at)}</td>
                    <td className="muted" style={{ fontSize: 12 }}>{formatAge(it.created_at)}</td>
                  </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination — cursor-based, only when sort is the default (created_at).
            For other sorts the hub returns next_cursor=null so the button hides. */}
        {!initialLoading && !error && cursor && isDefaultSort && (
          <div style={{ display: "flex", justifyContent: "center", padding: "8px 0" }}>
            <button className="btn" onClick={() => void loadMore()} disabled={loadingMore}>
              {loadingMore ? "Loading…" : "Load more"}
            </button>
          </div>
        )}
        {!initialLoading && !error && items.length > 0 && !isDefaultSort && (
          <div className="muted" style={{ fontSize: 12, padding: "4px 0" }}>
            Showing up to {PAGE_SIZE} items. Return to Age sort to page through older items.
          </div>
        )}
      </div>

      {dismissOpen && (
        <BulkDismissDialog
          items={selectedItems}
          onClose={() => setDismissOpen(false)}
          onDone={(res) => {
            setDismissOpen(false);
            setDismissResults(res.results);
            // Keep whatever the hub refused selected, so a second attempt
            // (or a look at the reason) doesn't start from an empty table.
            const stillSelected = new Set(
              res.results.filter((r) => r.outcome !== "dismissed").map((r) => r.id)
            );
            setSelected(stillSelected);
            void loadFirstPage();
          }}
        />
      )}
    </PageLayout>
  );
}

// ---------------------------------------------------------------------------
// Bulk dismissal — dialog + result panel
// ---------------------------------------------------------------------------

/**
 * Collect the ONE dismissal code and ONE closure note applied to the whole
 * selection, then POST them with the selected ids.
 *
 * Both fields are what the hub requires of any dismissal; this dialog asks
 * for them once instead of once per item. The hub still validates each id
 * on its own, so a refusal here is never the last word — the result panel
 * reports what actually happened per item.
 */
function BulkDismissDialog(props: {
  items: ActionItemRow[];
  onClose: () => void;
  onDone: (res: BulkDismissResponse) => void;
}) {
  const [codes, setCodes] = useState<ActionItemCode[]>([]);
  const [codesLoading, setCodesLoading] = useState(true);
  const [codeId, setCodeId] = useState("");
  const [notes, setNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    void (async () => {
      try {
        const res = await apiFetch<{ codes: ActionItemCode[] }>("/api/tenant/action-item-codes/dismissal-codes");
        setCodes(res.codes.filter((c) => c.active));
      } catch (e) {
        setError(e instanceof Error ? e.message : String(e));
      } finally {
        setCodesLoading(false);
      }
    })();
  }, []);

  // Items that have to pass through Acknowledged on their way out. The hub
  // walks that hop itself (it is the same path a user takes by hand), but
  // it writes real activity rows, so say so rather than let it surprise
  // someone reading the timeline later.
  const needsAck = props.items.filter((it) => it.status === "open" || it.status === "deferred").length;

  const canSubmit = !submitting && !codesLoading && codeId !== "" && notes.trim().length > 0 && props.items.length > 0;

  async function submit() {
    if (!canSubmit) return;
    setSubmitting(true);
    setError(null);
    try {
      const res = await apiFetch<BulkDismissResponse>("/api/tenant/action-items/bulk-dismiss", {
        method: "POST",
        body: {
          ids: props.items.map((it) => it.id),
          dismissal_code_id: codeId,
          closure_notes: notes.trim()
        }
      });
      props.onDone(res);
    } catch (e) {
      setError(e instanceof ApiRequestError ? e.message : e instanceof Error ? e.message : String(e));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="modalOverlay" role="presentation" onClick={submitting ? undefined : props.onClose}>
      <div
        className="modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="bulkDismissTitle"
        style={{ maxWidth: 560, width: "90%" }}
        onClick={(e) => e.stopPropagation()}
        onKeyDown={(e) => {
          if (e.key === "Escape" && !submitting) props.onClose();
        }}
      >
        <div style={{ padding: "14px 18px", borderBottom: "1px solid var(--border)" }}>
          <h2 id="bulkDismissTitle" style={{ margin: 0, fontSize: 16 }}>
            Dismiss {props.items.length} action item{props.items.length === 1 ? "" : "s"}
          </h2>
          <div className="muted" style={{ fontSize: 12, marginTop: 2 }}>
            The same dismissal code and note are recorded on every one of them.
          </div>
        </div>

        <div style={{ padding: "14px 18px", display: "grid", gap: 12 }}>
          <div style={{ maxHeight: 140, overflowY: "auto", display: "grid", gap: 2 }}>
            {props.items.map((it) => (
              <div key={it.id} style={{ fontSize: 12, display: "flex", gap: 8, alignItems: "center" }}>
                <span className={`pill ${statusPillClass(it.status)}`} style={{ fontSize: 10 }}>
                  {statusLabel(it.status)}
                </span>
                <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={it.title}>
                  {it.title}
                </span>
              </div>
            ))}
          </div>

          <label className="field">
            <span className="label">Dismissal code *</span>
            <select
              className="select"
              value={codeId}
              onChange={(e) => setCodeId(e.target.value)}
              disabled={submitting || codesLoading}
            >
              <option value="">{codesLoading ? "Loading…" : "Select a dismissal code…"}</option>
              {codes.map((c) => (
                <option key={c.id} value={c.id}>{c.label}</option>
              ))}
            </select>
            {!codesLoading && codes.length === 0 && (
              <div className="help">
                No dismissal codes are defined. An admin can add one under Admin → Action Item Codes.
              </div>
            )}
          </label>

          <label className="field">
            <span className="label">Closure notes *</span>
            <textarea
              className="input"
              rows={3}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              disabled={submitting}
              placeholder="Why are these being dismissed?"
            />
          </label>

          {needsAck > 0 && (
            <div className="help">
              {needsAck === props.items.length ? "These items are" : `${needsAck} of these items are`} not
              acknowledged yet, so each will be acknowledged and then dismissed — the same two steps as
              closing one by hand. Both show in the item's activity.
            </div>
          )}

          {error ? <div className="help" style={{ color: "var(--danger-text)" }}>{error}</div> : null}
        </div>

        <div
          style={{
            padding: "12px 18px",
            borderTop: "1px solid var(--border)",
            display: "flex",
            justifyContent: "flex-end",
            gap: 8
          }}
        >
          <button className="btn" onClick={props.onClose} disabled={submitting}>Cancel</button>
          <button className="btn btnDanger" onClick={() => void submit()} disabled={!canSubmit}>
            {submitting ? "Dismissing…" : `Dismiss ${props.items.length}`}
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * What actually happened, per item. A bulk dismissal routinely lands
 * partially — a caller can be Responsible on some of a selection and a
 * bystander on the rest — so the counts lead and every item that did NOT
 * get dismissed is named with its reason. Items are named by title; the ids
 * never reach the screen.
 */
function BulkDismissResultPanel(props: { results: BulkDismissResult[]; onClose: () => void }) {
  const summary = props.results.reduce(
    (acc, r) => { acc[r.outcome] = (acc[r.outcome] ?? 0) + 1; return acc; },
    {} as Record<BulkDismissResult["outcome"], number>
  );
  const notDismissed = props.results.filter((r) => r.outcome !== "dismissed");
  const allOk = notDismissed.length === 0;

  return (
    <div className="card" style={{ display: "grid", gap: 8 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <span className={`pill ${allOk ? "pillOk" : "pillWarn"}`}>{allOk ? "Done" : "Partly done"}</span>
        <strong style={{ fontSize: 13 }}>{summarizeBulkDismiss(summary)}</strong>
        <button
          type="button"
          className="btn"
          style={{ marginLeft: "auto" }}
          onClick={props.onClose}
          aria-label="Clear these results"
        >
          Clear
        </button>
      </div>
      {notDismissed.length > 0 && (
        <div style={{ display: "grid", gap: 2 }}>
          {notDismissed.map((r) => (
            <div key={r.id} style={{ fontSize: 12, display: "flex", gap: 8 }}>
              <span style={{ fontWeight: 600, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {r.title ?? "An item that no longer exists"}
              </span>
              <span className="muted">{bulkDismissOutcomeReason(r)}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Header cell: column name (sortable) + filter funnel + popover slot
// ---------------------------------------------------------------------------

function DueDateFilterPopover(props: {
  dueFrom: string;
  dueTo: string;
  includeNoDue: boolean;
  onApply: (next: { from: string; to: string; noDue: boolean }) => void;
}) {
  const [from, setFrom] = useState(props.dueFrom);
  const [to, setTo] = useState(props.dueTo);
  const [noDue, setNoDue] = useState(props.includeNoDue);
  useEffect(() => {
    setFrom(props.dueFrom);
    setTo(props.dueTo);
    setNoDue(props.includeNoDue);
  }, [props.dueFrom, props.dueTo, props.includeNoDue]);
  return (
    <>
      <div className="colFilterHeader">Filter by due date</div>
      <label className="field">
        <span className="label">From</span>
        <input type="date" className="input" value={from} onChange={(e) => setFrom(e.target.value)} />
      </label>
      <label className="field">
        <span className="label">To</span>
        <input type="date" className="input" value={to} onChange={(e) => setTo(e.target.value)} />
      </label>
      <label className="colFilterCheckbox">
        <input type="checkbox" checked={noDue} onChange={(e) => setNoDue(e.target.checked)} />
        <span>Include items with no due date</span>
      </label>
      <div className="colFilterActions">
        <button
          type="button"
          className="btn"
          onClick={() => props.onApply({ from: "", to: "", noDue: true })}
        >
          Clear
        </button>
        <button type="button" className="btn btnPrimary" onClick={() => props.onApply({ from, to, noDue })}>
          Apply
        </button>
      </div>
    </>
  );
}

// ---------------------------------------------------------------------------
// Small helpers
// ---------------------------------------------------------------------------

function describeDueFilter(from: string, to: string, includeNoDue: boolean): string {
  const parts: string[] = [];
  if (from && to) parts.push(`Due ${from} → ${to}`);
  else if (from) parts.push(`Due ≥ ${from}`);
  else if (to) parts.push(`Due ≤ ${to}`);
  if (!includeNoDue) parts.push("excluding no-due");
  else if (from || to) parts.push("including no-due");
  return parts.join(" · ") || "Due filter";
}

function MyRasciCell({ roles }: { roles: RASCIRole[] }) {
  const held = new Set<RASCIRole>(roles);
  return (
    <span style={{ display: "inline-flex", gap: 3, alignItems: "center" }}>
      {RASCI_ROLES.map((r) => {
        const active = held.has(r);
        return (
          <span
            key={r}
            className={active ? "pill pillOk" : "pill"}
            title={active ? roleLabel(r) : `Not in ${roleLabel(r)}`}
            style={{
              fontFamily: "var(--font-mono, monospace)",
              fontSize: 11,
              padding: "1px 5px",
              opacity: active ? 1 : 0.35,
              minWidth: 16,
              textAlign: "center"
            }}
          >
            {roleBadge(r)}
          </span>
        );
      })}
    </span>
  );
}
